import { handleActions } from 'redux-actions';
import { LOCATION_CHANGE } from 'react-router-redux';
import qs from 'qs';
import moment from 'moment';

import endpoints from '../../../config/endpoints';
import http from '../../shared/http';
import intl from '../../shared/intl';

import createFilters from '../../shared/filters';

import {
	getRequestQuery,
	mapQueryFilters,
	getCurrentCurrency,
	toggleInitialCurrency,
	initCurrencies,
	initDefaultCurrenciesAction
} from '../shared/utils';
import { REFETCH_ALL_DATA } from '../../root';

import cloneDeep from 'lodash/cloneDeep';

const filters = createFilters({
	namespace: 'systemAnalytics',
	stateNodeName: 'filters',
	filtersInitialState: [{
		title: 'Офферы',
		suggest: [{
			placeholder: 'Выберите оффер',
			key: 'offers',
			options: {
				source: `${endpoints.LOOKUPS.OFFERS}`,
				requestQuery: 'only_available=false',
				type: 'fromServer'
			}
		}]
	}]
});

const FETCH_DATA_SUCCESS = 'systemAnalytics/fetch-data-success';
const FETCH_DATA_ERROR = 'systemAnalytics/fetch-data-error';

const STATS_RESET = 'systemAnalytics/reset';
const STATS_APPLY_CALENDAR_VALUE = 'systemAnalytics/apply-calendar-value';
const STATS_UPDATE_QUERY = 'systemAnalytics/update-query';

const SHOW_PROGRESS_BAR = 'systemAnalytics/show-progress-bar';

const CHANGE_CURRENCY = 'systemAnalytics/change-currency';

const GET_LAST_CONVERSION_CURRENCY_SUCCESS = 'statistics/get-last-conversion-success';
const GET_LAST_CONVERSION_CURRENCY_ERROR = 'statistics/get-last-conversion-error';
const INIT_CURRENCIES = 'statistics/init-currencies';

const initialState = {
	data: null,
	query: {
		currency: null,
		groups: []
	},
	filters: filters.initialState,
	fullAppliedFilters: null,
	listOfCurrencies: [],
	showProgressBar: false,
	error: false
};

export const actions = Object.assign(
	{
		fetchData: props => (dispatch, getState) => {
			initDefaultCurrenciesAction(getState, dispatch, INIT_CURRENCIES, 'systemAnalytics');
			const request = http({
				url: `${endpoints.STATISTICS.GET_LAST_CONVERSION}`,
				method: 'get',
				actions: [
					GET_LAST_CONVERSION_CURRENCY_SUCCESS,
					GET_LAST_CONVERSION_CURRENCY_ERROR
				]
			});
			request(dispatch).then(() => fetchRequest(dispatch, getState, props));
		},

		statsApply: () => (dispatch, getState) => {
			dispatch({
				type: SHOW_PROGRESS_BAR
			});

			dispatch(actions.updateQuery());

			fetchRequest(dispatch, getState);
		},

		applyCalendarValue: calendarValue => ({
			type: STATS_APPLY_CALENDAR_VALUE,
			payload: calendarValue
		}),

		statisticsFilterApply: filterId => (dispatch, getState) => {
			filters.actions.filterApply(filterId)(dispatch, getState);
		},

		updateQuery: () => ({
			type: STATS_UPDATE_QUERY
		}),

		showProgressBar: () => ({
			type: SHOW_PROGRESS_BAR
		}),

		changeCurrency: currencyId => dispatch => {
			dispatch({
				type: CHANGE_CURRENCY,
				payload: currencyId
			});

			dispatch(actions.statsApply());
		}
	},
	filters.actions
);

const reducers = Object.assign(
	{
		[LOCATION_CHANGE]: (state, action) => {
			return initialState;
		},
		[FETCH_DATA_SUCCESS]: (state, action) => {
			const totalData = action.payload.data.totalStat;
			return {
				...state,
				data: {...totalData, showEmptyAgeChart: !Number(totalData.all_ages)},
				showProgressBar: false
			};
		},
		[FETCH_DATA_ERROR]: (state, action) => ({ ...state, data: null, error: true, showProgressBar: false }),
		[GET_LAST_CONVERSION_CURRENCY_ERROR]: (state, action) =>
			({ ...state, data: null, error: true, showProgressBar: false }),
		[STATS_APPLY_CALENDAR_VALUE]: (state, action) => {
			const dateRange = action.payload;
			const calendar = {
				...state.calendar,
				value: {
					start: (dateRange.start && moment(dateRange.start).format('YYYY-MM-DD')) || state.calendar.value.start,
					end: (dateRange.end && moment(dateRange.end).format('YYYY-MM-DD')) || state.calendar.value.end
				}
			};

			return { ...state, calendar };
		},

		[STATS_UPDATE_QUERY]: state => {
			const filters = state.filters && mapQueryFilters(state.filters);
			const fullFilters = state.filters && mapQueryFilters(state.filters, false);
			const date_range = state.calendar && state.calendar.value;

			return {
				...state,
				query: {
					...state.query,
					filters: filters || state.query.filters,
					date_range: date_range || state.query.date_range
				},
				fullAppliedFilters: fullFilters
			};
		},

		[STATS_RESET]: () => initialState,
		[REFETCH_ALL_DATA]: () => initialState,
		[SHOW_PROGRESS_BAR]: state => ({ ...state, showProgressBar: true }),
		[CHANGE_CURRENCY]: (state, action) => ({
			...state,
			query: {
				...state.query,
				currency: action.payload
			}
		}),
		[GET_LAST_CONVERSION_CURRENCY_SUCCESS]: (state, action) => toggleInitialCurrency(state, action),
		[INIT_CURRENCIES]: (state, action) => initCurrencies(state, action)
	},
	filters.reducers
);

export default handleActions(reducers, cloneDeep(initialState));

export const selectors = {
	getData: state => state.systemAnalytics.data,
	getDataTable: state => {
		const tableRows = state.systemAnalytics.data;

		return {
			tableRows,
			currency: state.app.userData && state.app.userData.activeBalance
		};
	},
	getFilters: state => filters.selectors.selectFilters(state => state.systemAnalytics.filters, state => state)(state),
	getAppliedFilters: state => filters.selectors.appliedFilters(state),
	getIntl: intl,
	getProgressBar: state => state.systemAnalytics.showProgressBar,
	getListOfCurrencies: state => state.app.userData.balances.map(item => ({
		id: item.currency,
		text: item.currency
	})),
	getActiveCurrency: state => getCurrentCurrency(state, 'systemAnalytics'),
	getError: state => state.systemAnalytics.error
};

function fetchRequest(dispatch, getState, props) {
	const state = getState();
	const activeBalance = getCurrentCurrency(state, 'systemAnalytics');
	const query = qs.stringify(
		getRequestQuery(
			state.systemAnalytics,
			activeBalance,
			false,
			false,
			false,
			state.statistics.masterSwitchConfig
		)
	);

	const request = http({
		url: `${endpoints.STATISTICS.SYSTEM_ANALYTICS}?${query}`,
		method: 'get',
		actions: [FETCH_DATA_SUCCESS, FETCH_DATA_ERROR]
	});

	request(dispatch);
}



// WEBPACK FOOTER //
// ./frontend/cabinets/modules/statistics/system-analytics/index.js